import { ReactNode } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  makeStyles,
  Tooltip,
} from '@material-ui/core';
import InfoIcon from '@material-ui/icons/InfoOutlined';
import { Skeleton } from '@material-ui/lab';
import { GenericField } from './GenericField';

const useStyles = makeStyles(() => ({
  listItem: {
    display: 'flex',
    alignItems: 'center',
  },
  listItemText: {
    margin: 0,
    alignItems: 'center',
    display: 'flex',
  },
  listItemIcon: {
    minWidth: '30px',
  },
  listSkeleton: {
    maxWidth: '300px',
  },
}));

export type GenericListItem = {
  icon: ReactNode;
  text: ReactNode;
  additionalInfo?: string;
};

export type GenericListProps = {
  label?: string;
  isLoading?: boolean;
  isError?: boolean;
  items?: GenericListItem[];
  error?: ReactNode;
  noData?: ReactNode;
};

const ListSkeleton = () => {
  const classes = useStyles();
  return (
    <Box className={classes.listSkeleton}>
      <Skeleton />
      <Skeleton />
      <Skeleton />
    </Box>
  );
};

export const GenericList = ({
  label,
  items = [],
  error,
  isLoading = false,
  isError = false,
  noData,
}: GenericListProps): JSX.Element => {
  const classes = useStyles();

  const renderList = () => (
    <List>
      {items.map((item, idx) => (
        <ListItem key={idx} className={classes.listItem}>
          <ListItemIcon className={classes.listItemIcon}>
            {item.icon}
          </ListItemIcon>
          <ListItemText
            primary={
              <Box display="flex" alignItems="center">
                {item.text}
                {item.additionalInfo && (
                  <Tooltip title={item.additionalInfo}>
                    <Box display="flex" alignItems="center" ml={1}>
                      <InfoIcon fontSize="small" />
                    </Box>
                  </Tooltip>
                )}
              </Box>
            }
            className={classes.listItemText}
          />
        </ListItem>
      ))}
    </List>
  );

  return (
    <GenericField
      label={label}
      value={
        <Box>
          {isLoading && <ListSkeleton />}
          {!isLoading && !isError && (items.length ? renderList() : noData)}
          {!isLoading && isError && error}
        </Box>
      }
    />
  );
};
